import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { trackEvent } from 'utils/analytics';
import { DriverTrends, trendRentalType, TrendRentalType } from 'types/PropertyInsights';
import { SpotInterface } from 'types';
import { HTTPError } from 'clients/HTTPClient';
import { propertyInsightsClient } from 'clients';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import lineGraphImg from 'assets/lineGraph.png';
import WidgetBox from 'components/widgets/WidgetBox';
import AGGroupedSelect from 'components/AGSelect/AGGroupedSelect/AGGroupedSelect';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import LineChartSkeleton from 'components/Skeletons/LineChartSkeleton';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import { buildGroups, getDataForDriverTrendsGraph, TrendPeriod, trendPeriodOptions } from '../helpers';

type DriverTrendsWidgetProps = {
    spot: SpotInterface;
    filters: {
        trendRentalType: TrendRentalType;
        trendPeriod: TrendPeriod;
    };
    urlUpdater: UrlUpdaterFunctions<{
        trendRentalType: TrendRentalType;
        trendPeriod: TrendPeriod;
    }>;
};

const options: ChartOptions<'line'> = {
    elements: {
        point: {
            backgroundColor: 'transparent',
            borderWidth: 0,
            hoverBorderWidth: 0,
        },
    },
    scales: {
        y: {
            grid: { display: true },
            ticks: { maxTicksLimit: 4 },
            min: 0,
            title: {
                display: true,
                text: 'Rentals',
            },
        },
    },
    interaction: {
        mode: 'index',
        intersect: false,
    },
};

const allRentalTypes = Object.values(trendRentalType) as TrendRentalType[];

function DriverTrendsWidget({ spot, filters, urlUpdater }: DriverTrendsWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [graphData, setGraphData] = useState<
        | ({
              labels: string[];
          } & { [key in TrendRentalType]: { data: number[]; backgroundColor: string; label: string } })
        | null
    >(null);
    const [report, setReport] = useState<DriverTrends | null>(null);

    const groups = useMemo(() => {
        if (!report) return [];
        return buildGroups(report);
    }, [report]);

    const datasets = useMemo(() => {
        if (!graphData) return [];
        const sets = [];
        const selectedRentalTypes = filters.trendRentalType
            ? (filters.trendRentalType.split(',') as TrendRentalType[])
            : allRentalTypes;
        for (const rentalType of allRentalTypes) {
            if (selectedRentalTypes.includes(rentalType)) sets.unshift(graphData[rentalType]);
        }
        return sets;
    }, [filters.trendRentalType, graphData]);

    function handleRentalTypeSelection(selection: string[], clickedItem: string, offOn: 'Off' | 'On') {
        if (selection.length === allRentalTypes.length) {
            urlUpdater.trendRentalType('');
        } else urlUpdater.trendRentalType(selection.join(','));
        trackEvent(`Toggle ${clickedItem} ${offOn}`, {
            spot: spot.pk,
            spotName: spot.name,
        });
    }

    const selectedRentalType = useMemo(() => {
        const selectedRentalTypes = filters.trendRentalType.split(',');
        // Checking trendRentalType is truthy to exclude case where filters.trendRentalType = '' ("All" is selected)
        if (filters.trendRentalType && selectedRentalTypes.length < allRentalTypes.length) return selectedRentalTypes;
        return allRentalTypes;
    }, [filters.trendRentalType]);

    function handleTrendPeriodSelection(selection: string) {
        urlUpdater.trendPeriod(selection);
        trackEvent('Changed Driver Trends Timeframe', {
            spot: spot.pk,
            spotName: spot.name,
            timeframe: selection,
        });
    }

    const getDriverTrends = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const date = new Date();
            const endDate = date.toISOString().split('T')[0];

            date.setMonth(date.getMonth() - (Number(filters.trendPeriod) - 1));
            date.setDate(1);
            const startDate = date.toISOString().split('T')[0];

            const response = await propertyInsightsClient.getDriverTrends(spot.uuid, startDate, endDate);

            setGraphData(getDataForDriverTrendsGraph(response));
            setReport(response);
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.trendPeriod]);

    useEffect(() => {
        getDriverTrends();
    }, [spot.uuid, getDriverTrends, filters.trendPeriod]);

    const isLoading = loading || !graphData || !report;
    const isEmpty = report && report.trends.length < 3;

    return (
        <WidgetBox
            title="Driver Trends"
            rightElement={
                <AGSimpleSelect
                    options={trendPeriodOptions}
                    selected={filters.trendPeriod}
                    onSelect={handleTrendPeriodSelection}
                    width="160px"
                />
            }
        >
            {isEmpty && !isLoading ? (
                <EmptyWidget imgUrl={lineGraphImg} />
            ) : errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getDriverTrends} />
            ) : (
                <div>
                    <AGGroupedSelect
                        groups={groups}
                        selectedValues={selectedRentalType}
                        onSelectionChange={handleRentalTypeSelection}
                        loading={isLoading}
                    />
                    {isLoading ? (
                        <div style={{ width: '100%', height: '200px' }}>
                            <LineChartSkeleton />
                        </div>
                    ) : (
                        <div style={{ paddingBlock: '20px' }}>
                            <Line
                                data={{
                                    labels: graphData.labels,
                                    datasets,
                                }}
                                options={options}
                                style={{ maxHeight: '260px' }}
                            />
                        </div>
                    )}
                </div>
            )}
        </WidgetBox>
    );
}

export default DriverTrendsWidget;
